export const en = {
  navigation: {
    seo: 'SEO',
    sea: 'SEA',
    marketing: 'Marketing',
    ai: 'AI',
    system: 'System',
    dashboard: 'Dashboard',
    seoAudit: 'Site Audit',
    keywords: 'Keywords',
    serp: 'SERP Monitor',
    backlinks: 'Backlinks',
    competitors: 'Competitors',
    seoSchedules: 'Schedules',
    campaigns: 'Campaigns',
    adPerformance: 'Ad Performance',
    contentGenerator: 'Content Generator',
    automation: 'Automation',
    reports: 'Reports',
    aiAdvisor: 'AI Advisor',
    settings: 'Settings',
    profile: 'Profile',
    logs: 'Logs',
    logout: 'Logout',
    // New navigation items
    projects: 'Projects',
    globalAI: 'Global AI',
    selectProject: 'Select project',
    home: 'Home',
    roadmap: 'Roadmap',
    templates: 'Templates',
    integrations: 'Integrations',
    admin: 'Admin',
    seoDashboard: 'SEO Dashboard',
    adsDashboard: 'Ads Dashboard',
    seoCrawls: 'Crawls',
    seaCampaigns: 'Campaigns',
    performance: 'Performance',
    shoppingCampaigns: 'Shopping',
    conversions: 'Conversions',
    audiences: 'Audiences',
    recommendations: 'Recommendations',
    negativeKeywords: 'Negative Keywords',
    searchTerms: 'Search Terms',
    auctionInsights: 'Auction Insights',
    products: 'Products',
    bidRules: 'Bid Rules',
    metaAds: 'Meta Ads',
    tiktokAds: 'TikTok Ads',
    // SEO navigation groups
    groupAuditsCrawl: 'Audits & Crawl',
    groupKeywords: 'Keywords',
    groupContent: 'Content',
    groupOffPage: 'Off-Page',
    groupAiPerformance: 'AI & Performance',
    keywordMapping: 'Keyword Mapping',
    keywordResearch: 'Keyword Research',
    contentOptimization: 'Content Optimization',
    migration: 'Migration / Compare',
    geo: 'GEO',
    seoTasks: 'Smart To-Do',
    visualAnalysis: 'Visual Analysis',
    irysIndex: 'Irys SEO Index',
  },
  // Projects section
  projects: {
    title: 'Projects',
    subtitle: 'Manage your SEO & marketing projects',
    newProject: 'New Project',
    createFirst: 'Create your first project',
    backToProjects: 'Back to Projects',
    empty: {
      title: 'No projects yet',
      description: 'Create your first project to start tracking SEO performance, managing campaigns, and connecting your marketing tools.',
    },
    wizard: {
      title: 'Create New Project',
      description: 'Configure your project settings, competitors, and data sources.',
      projectName: 'Project Name',
      projectNamePlaceholder: 'e.g. US Market Expansion',
      websiteUrl: 'Website URL',
      language: 'Primary Market / Language',
      selectLanguage: 'Select language',
      languageHint: 'This setting affects keyword tracking and local SEO analysis.',
      location: 'Target Country',
      selectLocation: 'Select country',
      urlStructure: 'URL Structure',
      singleLanguage: 'Single Language',
      subdirectory: 'Subdirectory (e.g. /de/, /en/)',
      subdomain: 'Subdomain (e.g. de.example.com)',
      crawlScope: 'Crawl Scope',
      crawlScopeHint: 'Only URLs matching this pattern will be crawled.',
      competitors: 'Competitor Websites',
      competitorUrl: 'Competitor URL',
      competitorsHint: 'We will analyze these sites for their structure, keywords, and strategies.',
      discover: 'Auto-Discover',
      discoverCostHint: 'Auto-Discover uses the paid DataForSEO Labs API — each lookup consumes credits from your DataForSEO account.',
      paidProvider: 'Paid DataForSEO API',
      discoverError: 'Could not discover competitors. Try adding them manually.',
      discoverEmpty: 'No competitors could be discovered automatically. Configure the SEO data provider or add competitors manually.',
      addUrlPlaceholder: 'Add competitor URL',
      editCompetitor: 'Edit competitor',
      saveCompetitor: 'Save competitor',
      suggestedCompetitors: 'Suggested Competitors',
      integrationsHint: 'Select the data sources you want to connect. You can configure these later in project settings.',
      createProject: 'Create Project',
      step3: {
        noConnections: 'No accounts connected yet',
        noConnectionsHint: 'You can connect data sources later in Project Settings.',
        goToIntegrations: 'Go to Integrations',
        connectedAs: 'Connected as',
        selectProperty: 'Select property (optional)',
        optional: 'optional',
      },
    },
  },
  // Mission Control (project dashboard)
  missionControl: {
    title: 'Mission Control',
    subtitle: 'Overview of your project performance and pending tasks',
    organicTraffic: 'Organic Traffic',
    paidTraffic: 'Paid Traffic',
    conversions: 'Conversions',
    seoScore: 'SEO Score',
    tasks: 'Priority Tasks',
    tasksDescription: 'AI-recommended actions to improve performance',
    integrations: 'Data Sources',
    integrationsDescription: 'Connected platforms and their sync status',
    noIntegrations: 'No integrations connected yet.',
    alertTitle: 'Action Required',
    setupAlert: 'Complete your project setup by connecting data sources.',
    warningAlert: 'Some metrics need attention. Review the issues below.',
    trafficOverview: 'Traffic Overview',
    seoHealth: 'SEO Health',
    campaignPerformance: 'Campaign Performance',
    aiRecommendations: 'AI Recommendations',
    recentActivity: 'Recent Activity',
    notConnected: 'Not Connected',
    connect: 'Connect',
    viewDetails: 'View Details',
    clicks: 'Clicks',
    impressions: 'Impressions',
  },
  // Global AI Assistant
  globalAI: {
    title: 'Global AI Assistant',
    subtitle: 'Cross-project insights and strategic recommendations',
    projectsConnected: 'projects connected',
    welcome: 'How can I help you today?',
    welcomeDescription: 'Ask me anything about your SEO, advertising, or marketing strategy across all your projects.',
    placeholder: 'Ask about your SEO, ads, or marketing strategy...',
    hint: 'Press Enter to send, Shift+Enter for new line',
    unavailable: 'AI service is currently unavailable. Please try again.',
  },
  // OAuth
  oauth: {
    connecting: 'Connecting...',
    success: 'Connected!',
    failed: 'Connection Failed',
    processingMessage: 'Please wait while we complete the authorization...',
    successMessage: 'Your account has been connected successfully. Redirecting...',
    genericError: 'Failed to connect your account',
    tryAgain: 'Please try again or contact support if the problem persists.',
    missingParams: 'Missing authorization code or state parameter',
    notAuthenticated: 'You must be logged in to connect integrations',
    unexpectedError: 'An unexpected error occurred',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome back',
    overview: 'Overview',
    seoScore: 'SEO Score',
    organicTraffic: 'Organic Traffic',
    keywordsTracked: 'Keywords Tracked',
    backlinksTotal: 'Total Backlinks',
    activeCampaigns: 'Active Campaigns',
    adSpend: 'Ad Spend',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    totalProjects: 'Total Projects',
    trackedKeywords: 'Tracked Keywords',
    backlinks: 'Backlinks',
    avgPosition: 'Avg. Position',
    noRecentActivity: 'No recent activity to display.',
    seoOverview: 'SEO Overview',
    campaignOverview: 'Campaign Overview',
    automationOverview: 'Automation Overview',
  },
  seo: {
    selectProject: 'Select a project to view SEO data.',
    dashboard: {
      title: 'SEO Dashboard',
      noData: 'No crawl data yet',
      loadError: 'Could not load dashboard data',
      noDataDesc: 'Run your first crawl to start tracking SEO performance over time.',
      runFirstCrawl: 'Run First Crawl',
      crawls: 'crawls',
      viewCrawls: 'View Crawls',
      currentScore: 'Current Score',
      keywordsTracked: 'Keywords Tracked',
      pagesTracked: 'Pages Tracked',
      lastCrawl: 'Last Crawl',
      scoreTrend: 'Score Trend',
      overall: 'Overall',
      titleScore: 'Title',
      metaScore: 'Meta',
      contentScore: 'Content',
      technicalScore: 'Technical',
      issueTrend: 'Issue Trend',
      critical: 'Critical',
      warnings: 'Warnings',
      info: 'Info',
      keywordTrends: 'Keyword Trends',
      keyword: 'Keyword',
      page: 'Page',
      position: 'Position',
      change: 'Change',
      clicks: 'Clicks',
      impressions: 'Impressions',
      rankingDistribution: 'Ranking Distribution',
    },
    audit: {
      title: 'Site Audit',
      newAudit: 'New Audit',
      runAudit: 'Run Audit',
      score: 'SEO Score',
      issues: 'Issues Found',
      pagesCrawled: 'Pages Crawled',
      critical: 'Critical',
      warnings: 'Warnings',
      passed: 'Passed',
      lastAudit: 'Last Audit',
      noAudits: 'No audits available yet. Run your first audit to see results.',
      startAudit: 'Start Audit',
      emptyTitle: 'No audits yet',
      emptyDesc: 'Run your first SEO audit to see results here',
      startAuditTitle: 'Start New Audit',
      startAuditDesc: 'We will crawl your website and analyse every page for SEO issues, performance, and content quality. This may take a few minutes depending on your site size.',
      editAuditTitle: 'Edit Crawl Settings',
      editAuditDesc: 'Update the crawl configuration. Changes apply to this crawl if it has not run yet, or serve as the template for the next crawl on this domain.',
      editSettings: 'Edit settings',
      auditTypeLabel: 'Audit Type',
      auditTypeFull: 'Full Audit',
      auditTypeTechnical: 'Technical Only',
      auditTypeContent: 'Content Only',
      configLabel: 'Crawl Settings',
      maxPagesLabel: 'Max Pages',
      maxPagesUnlimited: 'Unlimited',
      maxDepthLabel: 'Max Depth',
      maxDepthUnlimited: 'Unlimited',
      jsRenderingLabel: 'JavaScript Rendering',
      jsRenderingHint: 'Enable for React/Angular/Vue sites (slower)',
      includeSubdomainsLabel: 'Include Subdomains',
      includeGscLabel: 'Include Google Search Console data',
      includeGscHint: 'Enrich crawl results with GSC impressions, clicks & positions',
      crawlCompetitorsLabel: 'Also crawl competitors',
      crawlCompetitorsHint: 'Starts a separate audit for each configured competitor',
      advancedFeatures: 'Advanced Features (opt-in)',
      includeGa4Label: 'Google Analytics 4',
      includeGa4Hint: 'Fetch page engagement data (page views, bounce rate, sessions)',
      includeGa4NotConnected: '⚠ GA4 is not connected for this project — data will not be fetched.',
      includeCwvLabel: 'Core Web Vitals',
      includeCwvHint: 'Measure LCP, CLS, FCP during crawl (requires JS rendering)',
      includeSchemaValidationLabel: 'Schema Validation',
      includeSchemaValidationHint: 'Validate structured data against schema.org specs',
      includeMobileCheckLabel: 'Mobile-Friendly Check',
      includeMobileCheckHint: 'Check viewport meta tag and mobile compatibility',
      includeAiOverviewLabel: 'AI Overview Tracking',
      includeAiOverviewHint: 'Check if your domain appears in Google AI Overviews (via DataForSEO)',
      includeAiAnalysisLabel: 'AI-Powered Analysis',
      includeAiAnalysisHint: 'Advanced AI suggestions (⚠ costly for large shops — confirm first)',
      startButton: 'Start Audit',
      cancelButton: 'Cancel',
      cancelAudit: 'Cancel Audit',
      cancelAuditConfirm: 'Stop this audit?',
      statusPending: 'Pending',
      statusCrawling: 'Crawling',
      statusAnalyzing: 'Analyzing',
      statusScoring: 'Scoring',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      progressLabel: 'Progress',
      pagesProgress: '{crawled} / {total} pages',
      schedulesTitle: 'Scheduled Audits',
      noSchedules: 'No scheduled audits yet.',
      addSchedule: 'Add Schedule',
      editSchedule: 'Edit Schedule',
      deleteSchedule: 'Delete Schedule',
      scheduleFrequency: 'Frequency',
      scheduleDaily: 'Daily',
      scheduleWeekly: 'Weekly',
      scheduleMonthly: 'Monthly',
      scheduleDayOfWeek: 'Day of Week',
      scheduleTime: 'Time of Day',
      scheduleNextRun: 'Next Run',
      scheduleLastRun: 'Last Run',
      scheduleActive: 'Active',
      scheduleInactive: 'Inactive',
      scheduleToggle: 'Toggle Active',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      deleteScheduleConfirm: 'Delete this schedule?',
      targetDomainLabel: 'Domain to crawl',
      targetDomainProject: 'Your site',
      targetDomainCompetitor: 'Competitor',
      deleteAudit: 'Delete Audit',
      deleteAuditConfirmDesc: 'This will permanently delete this audit and all its data. This action cannot be undone.',
      cancelAuditConfirmDesc: 'This will stop the currently running crawl. Pages already crawled will be kept but the audit will be marked as failed.',
      confirmStop: 'Stop Audit',
      confirmDelete: 'Delete',
      convertToSchedule: 'Convert to Schedule',
      triggerNow: 'Run Now',
      deleteScheduleConfirmDesc: 'This will permanently delete this scheduled audit. This action cannot be undone.',
    },
    auditDetail: {
      title: 'Audit Detail',
      notFound: 'Audit not found.',
      overall: 'Overall',
      titleScore: 'Title',
      metaScore: 'Meta',
      contentScore: 'Content',
      technicalScore: 'Technical',
      headingScore: 'Headings',
      imageScore: 'Images',
      linkScore: 'Links',
      schemaScore: 'Schema',
      statusCode: 'Status',
      loadTime: 'Load Time',
      metaDescription: 'Meta Description',
      canonical: 'Canonical',
      internalLinks: 'Internal Links',
      externalLinks: 'External Links',
      brokenLinks: 'Broken Links',
      links: 'Links',
      issues: 'Issues',
      totalIssues: 'Total Issues',
      issueTypes: 'Issue Types',
      overviewTab: 'Overview',
      pagesTab: 'Pages',
      issuesTab: 'Issues',
      noPages: 'No page results found.',
      noIssues: 'No issues found — great!',
      page: 'Page',
      duration: 'Duration',
      scoreOverview: 'Score Overview',
      critical: 'Critical',
      warning: 'Warning',
      info: 'Info',
      avgLoadTime: 'Avg. Load Time',
      avgTTFB: 'Avg. TTFB',
      avgPageSize: 'Avg. Page Size',
      maxLoadTime: 'Max Load Time',
      indexable: 'Indexable',
      notIndexable: 'Not Indexable',
      redirects: 'Redirects',
      redirect: 'Redirect',
      inSitemap: 'In Sitemap',
      hasSchema: 'Has Schema',
      hasOpenGraph: 'Has Open Graph',
      crawlOverview: 'Crawl Overview',
      performance: 'Performance',
      issueSeverity: 'Issue Severity',
      issuesByCategory: 'Issues by Category',
      topIssues: 'Top Issues',
      scoreDistribution: 'Score Distribution',
      statusCodes: 'Status Codes',
      searchPages: 'Search pages...',
      pageSize: 'Page Size',
      wordCount: 'Word Count',
      textRatio: 'Text Ratio',
      seoMeta: 'SEO Meta',
      technicalDetails: 'Technical Details',
      crawlDepth: 'Crawl Depth',
      affectedPages: 'Affected pages',
      presets: 'Presets',
      clearFilters: 'Clear',
      presetNamePlaceholder: 'Filter preset name...',
      all: 'All',
      withIssues: 'With issues',
      noRedirect: 'No redirect',
      keywordsTab: 'Keywords',
      historyTab: 'History',
      engagementTab: 'Engagement',
      aiOverviewTab: 'AI Overview',
      siteStructureTab: 'Site Structure',
      technicalTab: 'Technical',
      gscSummary: 'Search Console Summary',
      gscTotalClicks: 'Total Clicks',
      gscTotalImpressions: 'Total Impressions',
      gscAvgPosition: 'Avg. Position',
      gscAvgCtr: 'Avg. CTR',
      gscPagesWithData: 'Pages with GSC Data',
      gscTopPages: 'Top Pages by Clicks',
      gscTopKeywords: 'Top Keywords',
      gscNoData: 'No Google Search Console data available for this audit.',
      gscNoDataWdf: 'No Google Search Console data for this audit — showing keywords detected from the crawl (WDF*IDF).',
      wdfIdfKeywords: 'Keywords detected in crawl (WDF*IDF)',
      wdfIdfScore: 'WDF*IDF',
      pagesFound: 'Pages',
      searchVolume: 'Volume',
      cannibalization: 'Keyword Cannibalization',
      loadCannib: 'Load',
      viewPages: 'View pages',
      noCannib: 'No cannibalization detected',
      gscPageUrl: 'Page URL',
      gscKeyword: 'Keyword',
      gscKeywordsTotal: 'keywords total',
      gscClicks: 'Clicks',
      gscImpressions: 'Impressions',
      gscCtr: 'CTR',
      gscPosition: 'Position',
      historyTitle: 'Audit History',
      historyNoData: 'No previous audits found for this domain.',
      historyScoreTrend: 'Score Trend',
      historyIssueTrend: 'Issue Trend',
      historyDate: 'Date',
      historyScore: 'Score',
      historyPages: 'Pages',
      historyIssues: 'Issues',
      historyView: 'View',
      // AI Overview
      aiOverviewTitle: 'AI Overview Visibility',
      aiOverviewDescription: 'Track whether your domain appears in Google AI Overview results for your keywords.',
      aiOverviewCheckNow: 'Check Now',
      aiOverviewKeywordsChecked: 'Keywords Checked',
      aiOverviewPresent: 'AI Overview Present',
      aiOverviewDomainCited: 'Domain Cited',
      aiOverviewCitationRate: 'Citation Rate',
      aiOverviewCitedOnly: 'Cited Only',
      aiOverviewResults: 'Results',
      aiOverviewKeyword: 'Keyword',
      aiOverviewStatus: 'AI Overview',
      aiOverviewCited: 'Cited',
      aiOverviewReferences: 'References',
      aiOverviewCheckedAt: 'Checked',
      aiOverviewActive: 'Present',
      aiOverviewNone: 'None',
      aiOverviewYes: 'Yes',
      aiOverviewNo: 'No',
      aiOverviewNoResults: 'No AI Overview data yet. Click "Check Now" to start.',
      aiOverviewNoCitedResults: 'No results where your domain is cited.',
      aiOverviewEmpty: 'No AI Overview Data',
      aiOverviewEmptyDescription: 'Check if your domain appears in Google AI Overview results. Track your visibility alongside AI-generated answers.',
      aiOverviewRunFirst: 'Run First Check',
      aiOverviewDataForSEONote: 'Powered by DataForSEO — checks tracked keywords for AI Overview presence.',
      // Engagement (GA4)
      engagementTitle: 'Page Engagement',
      engagementDescription: 'User engagement metrics from Google Analytics 4 for crawled pages.',
      engagementAvgDuration: 'Avg. Session Duration',
      engagementRate: 'Engagement Rate',
      engagementPageViews: 'Page Views',
      engagementActiveUsers: 'Active Users',
      engagementByPage: 'Engagement by Page',
      engagementPage: 'Page',
      engagementViews: 'Views',
      engagementEngRate: 'Eng. Rate',
      engagementBounce: 'Bounce',
      engagementDuration: 'Duration',
      engagementUsers: 'Users',
      engagementSessions: 'Sessions',
      engagementEmpty: 'No Engagement Data',
      engagementEmptyDescription: 'Connect Google Analytics and enable GA4 data in crawl settings to see engagement metrics.',
      engagementHowToEnable: 'Enable "Google Analytics 4" in the Advanced Features section when starting a new audit.',
      // Crawl health
      crawlHealth: 'Crawl Health',
      contentAnalysis: 'Content Analysis',
      metaKeywords: 'Meta Keywords',
      bodyTextPreview: 'Body Text Preview',
      keywordAnalysis: 'WDF*IDF Keyword Analysis',
      loadingKeywords: 'Loading keyword analysis...',
      noKeywords: 'No keyword data available. Run a new audit to generate WDF*IDF scores.',
      keyword: 'Keyword',
      cwvTitle: 'Core Web Vitals',
      cwvNoData: 'No CWV data — enable JavaScript rendering in crawl settings',
      lcp: 'LCP',
      lcpDesc: 'Largest Contentful Paint',
      cls: 'CLS',
      clsDesc: 'Cumulative Layout Shift',
      fcp: 'FCP',
      fcpDesc: 'First Contentful Paint',
      ttfb: 'TTFB',
      ttfbDesc: 'Time to First Byte',
      cwvGood: 'Good',
      cwvNeedsImprovement: 'Needs Improvement',
      cwvPoor: 'Poor',
      aiAnalysis: 'AI Analysis',
      analyzeWithAi: 'Analyze with AI',
      suggestMeta: 'Suggest Meta Tags',
      getRecommendations: 'Get SEO Recommendations',
      analyzing: 'Analyzing...',
      suggestedTitles: 'Suggested Titles',
      suggestedDescriptions: 'Suggested Descriptions',
      copied: 'Copied to clipboard',
      charCount: 'characters',
      contentScoreAi: 'Content Score',
      strengths: 'Strengths',
      weaknesses: 'Areas for Improvement',
      suggestions: 'Suggestions',
      aiUnavailable: 'AI analysis unavailable',
      copyToClipboard: 'Copy',
      readability: 'Readability',
      keywordDensity: 'Keyword Density',
      impact: 'Impact',
      effort: 'Effort',
      priority: 'Priority',
    },
    siteTree: {
      loading: 'Loading site structure...',
      error: 'Failed to load site structure',
      noData: 'No site structure data. Run an audit first.',
      noSearchResults: 'No pages match your search.',
      pages: 'pages',
      searchPlaceholder: 'Search URL, title or breadcrumb…',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      expand: 'Expand',
      collapse: 'Collapse',
      openPage: 'Open page',
      breadcrumbMode: 'Breadcrumb hierarchy active',
      maxBreadcrumbDepth: 'Max semantic depth',
      maxCrawlDepth: 'Max crawl depth',
      pagesWithBreadcrumbs: 'Pages with breadcrumbs',
      depthHintBreadcrumb: 'L = semantic level from breadcrumb trail (recommended for flat-URL shops like JTL). Home = L0, category = L1, subcategory = L2, product = L3+.',
      depthHintCrawl: 'D = BFS crawl depth from homepage. Home = D0, directly linked pages = D1, etc.',
      scoreDistribution: 'Score Distribution',
      pageTypes: 'Page Types',
      overallScore: 'Overall Score',
      url: 'URL',
      pageType: 'Page Type',
      depth: 'Depth',
      statusCode: 'Status Code',
      children: 'Sub-pages',
      breadcrumbTrail: 'Breadcrumb Trail',
      copyUrl: 'Copy URL',
      scoreGood: 'Good',
      scoreMedium: 'Needs improvement',
      scoreBad: 'Critical',
      scoreNone: 'Not scored',
      treeLabel: 'Site structure tree',
      closeDetail: 'Close details',
      treeView: 'Tree',
      graphView: 'Graph',
      filterByType: 'Filter by type',
      clearFilter: 'Clear filter',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitView: 'Fit view',
      expandNode: 'Expand children',
      collapseNode: 'Collapse children',
      nodeLimitWarning: 'Showing first {n} nodes — collapse some branches to see more',
      graphPanHint: 'Drag to pan · scroll to zoom',
    },
    technical: {
      links: 'Links',
      duplicates: 'Duplicates',
      redirects: 'Redirects',
      hreflang: 'Hreflang',
      schema: 'Schema',
      images: 'Images',
      resources: 'Resources',
      cwv: 'CWV',
      robotsTxt: 'robots.txt',
      sitemap: 'Sitemap',
      mobile: 'Mobile',
      schemaValidation: 'Schema Validation',
      internalLinks: 'Link Audit',
      redirectChains: 'Redirect Chains',
      hreflangTags: 'Hreflang Tags',
      imageAudit: 'Image Audit',
      jscssFonts: 'JS / CSS / Fonts',
      noData: 'No data available for this audit.',
      noDuplicates: 'No duplicate titles or meta descriptions found.',
      noRedirects: 'No redirect pages found.',
      noHreflang: 'No hreflang tags found.',
      noSchema: 'No schema markup found.',
      noImages: 'No images found.',
      noResources: 'No external resources found.',
      missingAltOnly: 'Missing alt only',
      renderBlockingOnly: 'Render-blocking only',
      pagesCrawled: 'Pages',
      uniquePages: 'Unique',
      httpsPages: 'HTTPS',
      nonHttpsPages: 'Non-HTTPS',
      avgCrawlDepth: 'Depth',
      avgPageSize: 'Page Size',
      avgLoadTime: 'Load Time',
      schemaPages: 'Schema',
    },
    cwv: {
      noData: 'No CWV Data',
      noDataDescription: 'Core Web Vitals are measured during crawl. Enable "Measure Core Web Vitals" in crawl settings for new audits.',
      distribution: 'Distribution',
    },
    robots: {
      notFound: 'No robots.txt Found',
      notFoundDescription: 'A robots.txt file helps control how search engines crawl your site. Consider adding one.',
    },
    sitemap: {
      title: 'Sitemap Analysis',
      notFound: 'No Sitemap Found',
      notFoundDescription: 'An XML sitemap helps search engines discover your pages. Consider creating one.',
      totalUrls: 'In Sitemap',
      crawled: 'Also Crawled',
      notCrawled: 'Not Crawled',
      orphanPages: 'Orphan Pages',
      coverage: 'Coverage',
      urls: 'Sitemap URLs',
    },
    mobile: {
      noData: 'No Mobile Data',
      noDataDescription: 'Enable "Mobile-Friendly Check" in crawl settings for new audits.',
      friendly: 'Mobile-Friendly',
      withIssues: 'Has Issues',
      missingViewport: 'No Viewport',
      issueTypes: 'Issue Types',
      pageResults: 'Page Results',
    },
    schemaValidation: {
      noData: 'No Schema Markups Found',
      noDataDescription: 'No structured data was detected on crawled pages.',
      total: 'Total Schemas',
      valid: 'Valid',
      invalid: 'Invalid',
      byType: 'By Type',
      results: 'Validation Results',
    },
    comparison: {
      title: 'Compare Crawls',
      selectPrevious: 'Select a previous crawl...',
      noPastAudits: 'No previous crawls available for comparison.',
      openPrevious: 'Open',
      results: 'Comparison Results',
      metric: 'Metric',
      previous: 'Previous',
      current: 'Current',
      change: 'Change',
    },
    cannibalization: {
      title: 'Keyword Cannibalization',
      found: '{count} keywords found on multiple pages',
      recommendedPage: 'Recommended primary page',
      competingPages: 'Competing pages',
      severity: {
        critical: 'Critical (3+ pages)',
        warning: 'Warning (2 pages)',
      },
      advice: 'Consider consolidating content or adding canonical tags',
      noConflicts: 'No keyword conflicts detected',
      pages: 'pages',
      wdfIdf: 'WDF*IDF',
      viewDetails: 'View Details',
      topOffenders: 'Top Cannibalization Issues',
      loadHint: 'Analyze keyword overlap across pages to find cannibalization issues.',
      loadButton: 'Load Cannibalization Analysis',
    },
    keywords: {
      title: 'Keyword Tracking',
      addKeyword: 'Add Keyword',
      addKeywords: 'Add Keywords',
      keyword: 'Keyword',
      position: 'Position',
      change: 'Change',
      volume: 'Volume',
      difficulty: 'Difficulty',
      url: 'Ranking URL',
      tracked: 'Tracked Keywords',
      improved: 'Improved',
      declined: 'Declined',
      noChange: 'No Change',
      addPlaceholder: 'Enter keyword to track...',
      add: 'Add Keyword',
      noKeywords: 'No keywords tracked yet. Add your first keyword above.',
      bulkImport: 'Bulk Import',
      bulkImportHint: 'Enter one keyword per line',
      import: 'Import',
      checkRanking: 'Check Ranking',
      checkRankingTriggered: 'Ranking check triggered',
      dataforseoConsentTitle: 'Enable paid DataForSEO SERP checks?',
      dataforseoConsentBody: 'SERP position checks use the paid DataForSEO API. Each keyword check consumes credits from your DataForSEO account. Costs are tracked in the API spending dashboard.',
      dataforseoConsentAccept: 'Enable & continue',
      dataforseoConsentDecline: 'Not now',
      lastChecked: 'Last Checked',
      emptyTitle: 'No keywords tracked',
      emptyDesc: 'Start tracking keywords to monitor your search rankings',
      competitorInsights: 'Competitor Keyword Insights',
      importKeywords: 'Import Keywords',
      manualBulk: 'Manual / Bulk',
      importFromGsc: 'Google Search Console',
      importFromDataForSeo: 'DataForSEO',
      gscImportHint: 'Fetches keywords live from Google Search Console. Falls back to latest audit data if GSC is not connected. Already tracked keywords are excluded.',
      dataforseoDiscoverHint: 'Discover keywords your domain ranks for via DataForSEO. Already tracked keywords are excluded.',
      dataforseoCostNotice: 'This uses the paid DataForSEO API — each discovery consumes credits from your DataForSEO account.',
      minClicks: 'Min Clicks',
      minImpressions: 'Min Impressions',
      maxPosition: 'Max Position',
      minVolume: 'Min Volume',
      searchFilter: 'Filter keywords...',
      loadGscKeywords: 'Load Keywords',
      discoverKeywords: 'Discover Keywords',
      discovering: 'Discovering...',
      keywordsFound: 'keywords found',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      importSelected: 'Import Selected',
      importing: 'Importing...',
      importSuccess: '{count} keywords imported',
      noGscData: 'Google Search Console is not connected or no keyword data available.',
      gscNoResults: 'Click "Load Keywords" to fetch keywords from Google Search Console.',
      noDataForSeoResults: 'Click "Discover Keywords" to find keywords your domain ranks for.',
      resultLimit: 'Limit',
      tags: 'Tags',
      priority: 'Priority',
      intent: 'Intent',
      branded: 'Branded keyword',
      targetUrl: 'Target URL (mapped)',
      currentUrl: 'Ranking URL (auto-detected)',
      noTargetUrl: 'No target URL set',
      notes: 'Notes',
      notesPlaceholder: 'Add notes about this keyword...',
      source: 'Source',
      tag: 'Tag',
      filters: 'Filters',
      clearFilters: 'Clear',
      keywordResearch: 'Keyword Research',
      seedKeywords: 'Seed Keywords',
      seedPlaceholder: 'e.g. seo tools, keyword research',
      modeSuggestions: 'Suggestions',
      modeIdeas: 'Ideas',
      runResearch: 'Research',
      cpc: 'CPC',
      competition: 'Competition',
      noResearchResults: 'Enter seed keywords and click "Research" to discover new keyword opportunities.',
      // Competitor pull tab (Phase 5)
      competitorPull: 'Competitor Keywords',
      competitorPullHint: 'Discover keywords your competitor ranks for via DataForSEO. Already tracked keywords are excluded.',
      selectCompetitor: 'Competitor',
      selectCompetitorPlaceholder: '— Select competitor —',
      noCompetitorResults: 'Click "Discover Keywords" to find keywords this competitor ranks for.',
      // Grouped view (Phase 4)
      viewFlat: 'Flat',
      viewGrouped: 'Grouped',
      switchToGrouped: 'Switch to grouped view',
      switchToFlat: 'Switch to flat view',
      sourceManual: 'Manual',
      showingFirst: 'Showing first',
      of: 'of',
      switchToFlatForAll: 'Switch to flat view to see all.',
      // Lifecycle status labels
      discoveryStatus: 'Status',
      status_discovered: 'Discovered',
      status_rediscovered: 'Rediscovered',
      status_active: 'Active',
      status_lost: 'Lost',
      // Source group filter
      sourceGroup: 'Group',
      // GSC sync button
      syncGsc: 'Sync GSC',
      syncGscTitle: 'Sync keywords from the latest GSC crawl data',
      gscPullTitle: 'Pull every keyword per URL live from Google Search Console (free, unlimited)',
      gscPulled: 'Pulled {{n}} keywords across {{p}} pages from GSC',
      gscPulledNone: 'GSC pull completed — no new keyword data found',
      gscPullError: 'GSC pull failed. Is Google Search Console connected?',
      // Keyword detail panel
      lifecycle: 'Lifecycle',
      status: 'Status',
      firstSeen: 'First seen',
      lastSeen: 'Last seen',
      sourceDomain: 'Domain',
      matchingPages: 'Matching Pages (GSC)',
      matchingPageUrl: 'URL',
      clicks: 'Clicks',
      impressions: 'Impr.',
      ctr: 'CTR',
      noPagesFound: 'No matching pages found in GSC data.',
      crawlFallback: 'No GSC data — showing WDF*IDF crawled terms instead.',
      // View modes (Phase 1)
      keywordView: 'Keywords',
      urlView: 'By URL',
      urls: 'URLs',
      urlViewEmpty: 'No URL data available. Run a crawl with GSC access, or sync GSC keywords.',
      urlSearchPlaceholder: 'Filter by URL or keyword…',
      cannibalizedWarning: 'Cannibalization: also ranking for',
      keywords: 'Keywords',
      // Pending review queue (Phase 1)
      reviewQueue: 'Review',
      reviewQueueTitle: 'Pending keyword suggestions',
      reviewQueueDesc: 'Generated from crawl WDF*IDF analysis. Accept to track, or ignore.',
      reviewQueueEmpty: 'No pending suggestions.',
      reviewAccept: 'Accept',
      reviewIgnore: 'Ignore',
      reviewAcceptAll: 'Accept All',
      reviewIgnoreAll: 'Ignore All',
      reviewAccepted: 'Keyword accepted',
      reviewIgnored: 'Keyword ignored',
      reviewAllAccepted: 'All suggestions accepted',
      reviewAllIgnored: 'All suggestions ignored',
      // SERP history (Phase 1)
      noSerpHistory: 'No position history yet. Run "Check Now" to start tracking.',
      latestCheck: 'latest',
      best: 'Best',
      worst: 'Worst',
      checks: 'checks',
      date: 'Date',
      rankingUrl: 'Ranking URL',
      serpFeatures: 'SERP',
      addTag: '+ add tag',
      checkNow: 'Check Now',
      openInSerp: 'Open in SERP Monitor',
    },
    keywordResearch: {
      title: 'Keyword Research',
      subtitle: 'Discover keyword opportunities for your domain — analyze content, exploit quick wins, beat competitors, and expand with AI.',
      wdfAnalysis: 'WDF*IDF Analysis',
      competitorGap: 'Competitor Gap',
      quickWins: 'Quick Wins',
      aiDiscovery: 'AI Discovery',
      wdfAnalysisDesc: 'URL-level content analysis. Select any crawled page to see its most important terms by WDF*IDF score — the terms that define what this page is about. Use this to optimize content focus or find missing terms.',
      gapDesc: 'Keywords your competitors currently rank for that you are not yet tracking. The most direct source of new opportunities — fully automated from your competitor list.',
      quickWinsDesc: 'Keywords where you already rank on page 1 (positions 4–10). These are your best low-hanging fruit — a targeted content improvement on the ranking page can push them into the top 3.',
      ideasDesc: 'Auto-seeded from your project domain. Discover related keyword opportunities via DataForSEO — edit the seeds for more targeted results.',
      selectUrl: 'Select a page to analyze',
      urlSearchPlaceholder: 'Type to filter pages by URL...',
      noPagesFound: 'No crawled pages found. Run a crawl audit first.',
      noWdfTerms: 'No content terms found for this page.',
      selectUrlHint: 'Select a page from the list above to see its WDF*IDF content analysis.',
      termsFound: 'terms',
      quickWinsCount: 'quick-win keywords from GSC',
      noQuickWins: 'No quick-win keywords found. This requires Google Search Console to be connected and keywords ranking at positions 4–10.',
      noQuickWinsNoGsc: 'No GSC data found. Make sure Google Search Console is connected to this project in Settings.',
      noQuickWinsTopRanker: 'GSC returned {{count}} keywords — but all of them rank in the top 3. Excellent! Look for new opportunities in the AI Discovery or Competitor Gap tabs.',
      noQuickWinsGscError: 'Could not load GSC data. Check your Google Search Console connection in Settings.',
      gscLive: 'Live GSC',
      gscAudit: 'Audit data',
      gscAuditFallback: 'Using data from the last crawl audit (live GSC connection unavailable).',
      requiresDataForSeo: 'Requires DataForSEO',
      gapLimitLabel: 'Max results',
      gapCta: 'Select a competitor above and click "Discover Gap Keywords" to start.',
      gapApiError: 'Discovery failed — DataForSEO returned an error:',
      noGapResultsEmpty: 'No gap keywords found for this competitor. They may rank for keywords you already track, or DataForSEO has no data for their domain.',
      ideasCta: 'Enter seed keywords above and click Discover.',
      ideasApiError: 'Research failed — DataForSEO returned an error:',
      noQuickWinsLive: 'GSC is connected but no keywords ranking at positions 1–10 were found. Your site may rank below page 1, or GSC hasn\'t accumulated enough impressions data yet.',
      noQuickWinsAudit: 'Live GSC connection is unavailable and the last crawl audit has no GSC data for positions 1–10. Make sure a crawl with GSC access has been completed.',
      refreshKeywords: 'Refresh',
      seedLabel: 'Seeds (comma-separated)',
      seedPlaceholder: 'e.g. keyword research, seo tool, rank tracking...',
      broadIdeasMode: 'Broad ideas mode (more diverse results)',
      autoSeeded: 'Auto-seeded from your domain: {{domain}}',
      selectCompetitor: 'Competitor',
      noCompetitors: 'No competitors tracked yet. Add competitors in the Competitors section first.',
      minVolume: 'Min. Volume',
      discoverGap: 'Discover Gap Keywords',
      discovering: 'Discovering...',
      discover: 'Discover',
      research: 'Research',
      researching: 'Researching...',
      addToTracking: 'Add',
      addAll: 'Add All',
      added: 'Added',
      keyword: 'Keyword',
      wdfIdf: 'WDF*IDF',
      inTitle: 'Title',
      inH1: 'H1',
      searchVolume: 'Volume',
      cpc: 'CPC',
      competition: 'Competition',
      clicks: 'Clicks',
      impressions: 'Impressions',
      position: 'Position',
      landingPage: 'Landing Page',
      gapResultsFor: 'gap keywords for',
      filterPlaceholder: 'Filter keywords...',
      noResults: 'No results found. Try different seed keywords or check that DataForSEO is configured.',
      noGapResults: 'No gap keywords found. Click "Discover Gap Keywords" to start, or verify DataForSEO is configured.',
      addToMap: "Add to map",
      aiResearch: "AI Research",
      llmDesc: "See whether AI assistants mention you for your keywords.",
      opportunities: "Opportunities",
    },
    serp: {
      title: 'SERP Monitor',
      history: 'Position History',
      snapshot: 'SERP Snapshot',
      features: 'SERP Features',
      localPack: 'Local Pack',
      featuredSnippet: 'Featured Snippet',
      peopleAlsoAsk: 'People Also Ask',
      noData: 'No SERP data available yet.',
      rankingDistribution: 'Ranking Distribution',
      positions1_3: 'Pos 1-3',
      positions4_10: 'Pos 4-10',
      positions11_20: 'Pos 11-20',
      positions21_50: 'Pos 21-50',
      positions51Plus: 'Pos 51+',
      notRanked: 'Not Ranked',
      positionHistory: 'Position History',
      noHistory: 'No position history available',
      serpFeatures: 'SERP Features',
      bigImprovement: 'Big improvement',
      bigDecline: 'Big decline',
      improved: 'Improved',
      declined: 'Declined',
      stable: 'Stable',
      newRankings: 'New',
      lostRankings: 'Lost',
      days30: '30 days',
      days90: '90 days',
      days180: '180 days',
      notEnoughData: 'Not enough data for trend chart',
      positionOverTime: 'Position over time',
      dataforseoConsentTitle: 'Enable paid DataForSEO SERP checks?',
      dataforseoConsentBody: 'SERP position checks use the paid DataForSEO API. Each keyword check consumes credits from your DataForSEO account. Costs are tracked in the API spending dashboard.',
      dataforseoConsentAccept: 'Enable & continue',
      dataforseoConsentDecline: 'Not now',
    },
    backlinks: {
      title: 'Backlink Profile',
      emptyTitle: 'No backlink data yet',
      emptyDesc: 'Connect a data source or run a backlink check to discover your link profile',
      overview: 'Overview',
      referringDomains: 'Referring Domains',
      anchorTexts: 'Anchor Texts',
      topPages: 'Top Pages',
      history: 'History',
      totalBacklinks: 'Total Backlinks',
      totalReferring: 'Referring Domains',
      dofollowPercent: 'Dofollow %',
      brokenLinks: 'Broken Links',
      triggerCheck: 'Check Now',
      checking: 'Checking...',
      domain: 'Domain',
      rank: 'Rank',
      backlinks: 'Backlinks',
      linksTo: 'Links to',
      dofollow: 'Dofollow',
      firstSeen: 'First Seen',
      anchor: 'Anchor Text',
      pageUrl: 'Page URL',
      date: 'Date',
      newBacklinks: 'New',
      lostBacklinks: 'Lost',
      noData: 'No data available',
      domainAuthority: 'Domain Authority',
      anchorText: 'Anchor Text',
      sourceUrl: 'Source URL',
      targetUrl: 'Target URL',
      status: 'Status',
      noBacklinks: 'No backlinks found yet.',
      source: 'Source',
      target: 'Target',
      authority: 'Authority',
      type: 'Type',
      fetchFromApi: 'Fetch from DataForSEO',
      fetchSettings: 'Fetch Settings',
      numberOfResults: 'Number of Results',
      fetchNow: 'Fetch Now',
      fetching: 'Fetching...',
      fetchSuccess: 'Backlinks fetched successfully',
      fetchHint: 'Fetches backlink data from DataForSEO and saves to database',
      lastFetched: 'Last Fetched',
      lastFetchCost: 'last fetch cost',
      costLabel: 'cost',
      costNotice: 'Uses the paid DataForSEO API. The exact cost of this fetch is logged to the API spend dashboard.',
      dataforseoConsentTitle: 'Enable paid DataForSEO backlink analysis?',
      dataforseoConsentBody: 'Backlink fetching and checks use the paid DataForSEO API. Each fetch consumes credits from your DataForSEO account. Costs are tracked in the API spending dashboard.',
      dataforseoConsentAccept: 'Enable & continue',
      dataforseoConsentDecline: 'Not now',
      storedResults: 'Stored',
      linkType: 'Link Type',
      // Competitor mode
      viewingOwn: 'Own Domain',
      viewingCompetitor: 'Competitor',
      switchToOwn: 'Switch to own domain',
      selectCompetitor: 'Select domain to analyze',
      allDomains: 'All domains',
      noCompetitors: 'No competitors tracked yet. Add competitors on the Competitors page.',
      competitorBacklinks: 'Competitor Backlinks',
      // Filters
      filters: 'Filters',
      minAuthority: 'Min. Authority',
      allLinkTypes: 'All types',
      allStatuses: 'All statuses',
      active: 'Active',
      lost: 'Lost',
      broken: 'Broken',
      // Tabs
      compare: 'Compare',
      backlinkGap: 'Backlink Gap',
      // Compare view
      compareTitle: 'Backlink Comparison',
      compareEmpty: 'Select competitors to compare with your domain.',
      compareSelectHint: 'Add competitors above to include them in the comparison',
      ownDomain: 'Own Domain',
      avgAuthority: 'Avg. Authority',
      // Backlink gap
      backlinkGapTitle: 'Backlink Gap',
      backlinkGapDesc: 'Domains linking to competitor but not to you — link building opportunities.',
      backlinkGapEmpty: 'No gap data. Select a competitor and fetch their backlink data first.',
      opportunity: 'Opportunity',
      // Fetch for competitor
      fetchForCompetitor: 'Fetch for {domain}',
      confirmFetchCompetitor: 'This will fetch and store backlinks for competitor {domain}. Continue?',
    },
    competitors: {
      title: 'Competitor Analysis',
      addCompetitor: 'Add Competitor',
      domain: 'Domain',
      organicKeywords: 'Keywords',
      organicTraffic: 'Organic Traffic',
      domainAuthority: 'DA',
      overlapScore: 'Keyword Overlap',
      comparison: 'Comparison',
      addPlaceholder: 'e.g. competitor-domain.com',
      add: 'Add',
      addSuccess: 'Competitor added',
      noCompetitors: 'No competitors tracked yet. Add a competitor URL above.',
      empty: 'No competitors tracked. Add a competitor domain above or manage them in the Competitors page.',
      discoverMore: 'Discover More',
      discoverCostNotice: 'This uses the paid DataForSEO Labs API — each discovery consumes credits from your DataForSEO account.',
      discovering: 'Discovering...',
      addSelected: 'Add Selected',
      enrich: 'Enrich',
      enriching: 'Enriching...',
      keywordOverlap: 'Keyword Overlap',
      contentGap: 'Content Gap',
      backlinkGap: 'Backlink Gap',
      keyword: 'Keyword',
      searchVolume: 'Search Vol.',
      yourPosition: 'Your Pos.',
      competitorPosition: 'Comp. Pos.',
      cpc: 'CPC',
      referringDomain: 'Domain',
      rank: 'Rank',
      backlinks: 'Backlinks',
      showMore: 'Show more...',
      noData: 'No data available',
      emptyTitle: 'No competitors added',
      emptyDesc: 'Add competitor domains to track their performance',
      startAudit: 'Start audit for this competitor',
      auditStarted: 'Audit started for {domain}',
      manageAll: 'Manage Competitors',
      projectBadge: 'Project',
      discoveredBadge: 'Discovered',
      projectLevel: 'From project settings',
      removeFromProject: 'Project-level competitors must be removed from Project Settings.',
      promotedToProject: 'Competitor added to project settings',
      addToProject: 'Add to Project',
      promoteToProject: 'Also add to project-level competitors',
      projectCompetitors: 'Project-level',
      seoCompetitors: 'SEO-only',
      confirmDelete: 'Are you sure you want to remove this competitor? All gap analysis data will be lost.',
      dataforseoConsentTitle: 'Enable paid DataForSEO analysis?',
      dataforseoConsentBody: 'Competitor discovery, enrichment, keyword overlap and content gap use the paid DataForSEO Labs API. Each lookup consumes credits from your DataForSEO account. Costs are tracked in the API spending dashboard.',
      dataforseoConsentAccept: 'Enable & continue',
      dataforseoConsentDecline: 'Not now',
      addManual: 'Add manually',
      discover: 'Discover',
      backlinkGapNotice: 'Backlink gap shows referring domains cached from your last backlink fetch — refresh via a backlink fetch on the Backlink Profile page.',
      exportReport: 'Export Report',
      masterSourceNotice: 'Your own domain uses Google Search Console (organic traffic) and your tracked keywords, while competitors use DataForSEO estimates. Sources differ, so compare trends rather than absolute values.',
    },
    competitorPull: {
      title: 'Competitor Pull',
      tabKeywords: 'Keyword Discovery',
      tabAudit: 'Full Site Crawl',
      dataSource: 'Data Source',
      modeDomain: 'Domain Keywords',
      modeDomainDesc: 'All keywords the domain ranks for',
      modeUrlList: 'Page Keywords',
      modeUrlListDesc: 'Keywords for specific pages',
      urlList: 'URLs (one per line)',
      urlListHint: 'Enter full URLs of competitor pages to analyze (max 50)',
      keywordsPerUrl: 'Max keywords per URL',
      keywordLimit: 'Keyword limit',
      positionMin: 'Min. position',
      positionMax: 'Max. position',
      minVolume: 'Min. search volume',
      loadPreview: 'Load Preview',
      loading: 'Loading preview...',
      previewFound: '{n} keywords found',
      previewEmpty: 'No keywords found for these filters.',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      posHeader: 'Pos.',
      scheduleSection: 'Recurring Schedule',
      scheduleHint: 'Automatically re-run this keyword pull on a schedule and import new keywords.',
      schedWeekly: 'Weekly',
      schedMonthly: 'Monthly',
      schedDay: 'Day',
      schedTime: 'Time (UTC)',
      scheduleSaved: 'Schedule saved',
      importN: 'Import {n} keywords',
      importButton: 'Import Keywords',
      importSuccess: '{n} keywords imported',
      auditDesc: 'Start a full site crawl of the competitor domain to analyze their page structure, content, and technical SEO.',
      auditTarget: 'Target domain',
      auditType: 'Crawl type',
      auditTypeFull: 'Full crawl',
      startAudit: 'Start Full Crawl',
      starting: 'Starting...',
    },
    schedule: {
      title: 'Schedule',
      runNow: 'Run now',
      lastRun: 'Last run',
      nextRun: 'Next run',
      keywordCheckDesc: 'Weekly keyword checks let you spot ranking drops early and react before traffic loss.',
      backlinkCheckDesc: 'Regular backlink monitoring helps you detect lost links and new link building opportunities.',
      competitorEnrichDesc: 'Periodic competitor data refresh keeps your competitive intelligence up to date.',
      recentRuns: 'Recent Runs',
      noRecentRuns: 'No task executions yet',
    },
    issues: {
      tracking: 'Issue Tracking',
      statusLabel: 'Status',
      statusNew: 'New',
      statusAcknowledged: 'Acknowledged',
      statusInProgress: 'In Progress',
      statusFixed: 'Fixed',
      statusWontFix: "Won't Fix",
      statusReopened: 'Reopened',
      fixedSinceLastAudit: 'Fixed since last audit',
      newIssue: 'New issue',
      persisting: 'Persisting',
      regression: 'Regression',
      bulkActions: 'Bulk Actions',
      selectAll: 'Select All',
      updateStatus: 'Update Status',
      resolutionProgress: 'Issue Resolution Progress',
      resolved: 'resolved',
      noTrackedIssues: 'No tracked issues',
      addNotes: 'Add notes...',
    },
    pageHealth: {
      title: 'Page Health Profile',
      backlinksToPage: 'Backlinks to this page',
      keywordsTargeting: 'Keywords targeting this page',
      gscPerformance: 'Search Console Performance',
      noBacklinks: 'No backlinks found for this page',
      noKeywords: 'No tracked keywords point to this page',
      noGscData: 'No Search Console data available',
      healthScore: 'Health Score',
      clicks: 'Clicks',
      impressions: 'Impressions',
      avgPosition: 'Avg Position',
      topQueries: 'Top Queries',
    },
    alerts: {
      title: 'SEO Alerts',
      positionDrop: '{keyword} dropped {count} positions (now #{position})',
      positionGain: '{keyword} gained {count} positions (now #{position})',
      lostRanking: '{keyword} lost ranking entirely',
      newRanking: '{keyword} appeared at position #{position}',
      backlinksLost: '{count} backlinks lost',
      scoreDrop: 'Audit score dropped from {old} to {new}',
      allClear: 'All clear — no alerts',
      markRead: 'Mark as read',
      clearAll: 'Clear all',
      unread: 'unread',
    },
  },
  ads: {
    dashboard: {
      title: 'Ads Dashboard',
      comingSoon: 'Coming Soon',
      comingSoonDesc: 'The Ads Dashboard is currently under development and will be available soon.',
    },
  },
  campaigns: {
    manager: {
      title: 'Campaign Manager',
      newCampaign: 'New Campaign',
      name: 'Campaign Name',
      platform: 'Platform',
      status: 'Status',
      budget: 'Budget',
      startDate: 'Start Date',
      endDate: 'End Date',
      impressions: 'Impressions',
      clicks: 'Clicks',
      conversions: 'Conversions',
      ctr: 'CTR',
      cpc: 'CPC',
      roas: 'ROAS',
      create: 'Create Campaign',
      noCampaigns: 'No campaigns yet. Create your first campaign to get started.',
    },
    ads: {
      title: 'Ad Performance',
      adGroups: 'Ad Groups',
      adCopy: 'Ad Copy',
      headline: 'Headline',
      description: 'Description',
      displayUrl: 'Display URL',
      performance: 'Performance',
      abTesting: 'A/B Testing',
      winner: 'Winner',
      confidence: 'Confidence',
      noAds: 'No ads found.',
      name: 'Ad Name',
      impressions: 'Impressions',
      clicks: 'Clicks',
      ctr: 'CTR',
      cost: 'Cost',
    },
    budgets: {
      title: 'Budget Management',
      daily: 'Daily Budget',
      total: 'Total Budget',
      spent: 'Spent Today',
      remaining: 'Remaining',
      forecast: 'Monthly Forecast',
      overPace: 'Over pace',
      activeCampaigns: 'active campaigns',
      alertsCount: 'Active Alerts',
      activeAlerts: 'Budget Alerts',
      burnRate: 'Burn Rate',
      noAlerts: 'No Budget Alerts',
      allGood: 'All campaigns are within budget thresholds.',
    },
    adGroups: {
      title: 'Ad Groups',
      newAdGroup: 'New Ad Group',
      campaign: 'Campaign',
      defaultBid: 'Default Bid',
    },
    seaKeywords: {
      title: 'SEA Keywords',
      keyword: 'Keyword',
      matchType: 'Match Type',
      bid: 'Bid',
      qualityScore: 'Quality Score',
      addKeyword: 'Add Keyword',
      exact: 'Exact',
      phrase: 'Phrase',
      broad: 'Broad',
    },
  },
  sea: {
    performance: {
      title: 'Campaign Performance',
      subtitle: 'Track and analyze your SEA campaign metrics',
      selectCampaign: 'Select Campaign',
      dateRange: 'Date Range',
      last7d: 'Last 7 Days',
      last30d: 'Last 30 Days',
      last90d: 'Last 90 Days',
      impressions: 'Impressions',
      clicks: 'Clicks',
      ctr: 'CTR',
      spend: 'Spend',
      conversions: 'Conversions',
      roas: 'ROAS',
      date: 'Date',
      dailyBreakdown: 'Daily Breakdown',
      noData: 'No performance data available. Run a performance sync first.',
    },
    sync: {
      title: 'Google Ads Sync',
      customerId: 'Customer ID',
      syncNow: 'Sync Now',
      syncing: 'Syncing...',
      taskId: 'Task ID',
      status: 'Status',
      lastSynced: 'Last Synced',
      pushToGoogle: 'Push to Google',
      pushing: 'Pushing...',
      noCampaigns: 'No campaigns found. Sync your Google Ads account first.',
    },
    keywordResearch: {
      title: 'Keyword Research',
      seedKeywords: 'Seed Keywords',
      seedPlaceholder: 'Enter keywords, comma separated',
      plannerTab: 'Keyword Planner',
      aiTab: 'AI Expansion',
      research: 'Research',
      expand: 'Expand',
      researching: 'Researching...',
      expanding: 'Expanding...',
      keyword: 'Keyword',
      avgSearches: 'Avg. Monthly Searches',
      competition: 'Competition',
      bidLow: 'Bid (Low)',
      bidHigh: 'Bid (High)',
      intent: 'Intent',
      matchType: 'Match Type Suggestion',
      estCompetition: 'Est. Competition',
      noResults: 'No results yet. Enter seed keywords and click Research or Expand.',
    },
    negativeKeywords: {
      title: 'Negative Keywords',
      keyword: 'Keyword',
      matchType: 'Match Type',
      addNegative: 'Add Negative Keyword',
      placeholder: 'Enter keyword',
      exact: 'Exact',
      phrase: 'Phrase',
      broad: 'Broad',
      suggestNegatives: 'Suggest Negatives',
      suggesting: 'Suggesting...',
      suggestions: 'AI Suggestions',
      noKeywords: 'No negative keywords yet.',
      add: 'Add',
    },
    searchTerms: {
      title: 'Search Terms Report',
      searchTerm: 'Search Term',
      matchedKeyword: 'Matched Keyword',
      matchType: 'Match Type',
      impressions: 'Impressions',
      clicks: 'Clicks',
      conversions: 'Conversions',
      cost: 'Cost',
      isNegative: 'Negative',
      addNegative: 'Add as Negative',
      noTerms: 'No search terms found.',
      negativeSuggestions: 'Negative Keyword Suggestions',
      addAsNegative: 'Add as Negative',
      noSuggestions: 'No negative keyword suggestions found.',
      added: 'Added',
      reasonHighCost: 'High cost, no conversions',
      reasonLowCTR: 'High impressions, no clicks',
      reasonHighCPA: 'High cost per conversion',
    },
    auctionInsights: {
      title: 'Auction Insights',
      competitor: 'Competitor',
      impressionShare: 'Impression Share',
      overlapRate: 'Overlap Rate',
      outrankingShare: 'Outranking Share',
      positionAbove: 'Position Above Rate',
      topOfPage: 'Top of Page Rate',
      absTopOfPage: 'Abs. Top of Page Rate',
      noInsights: 'No auction insights available.',
    },
    products: {
      title: 'Product Manager',
      merchantId: 'Merchant ID',
      syncProducts: 'Sync Products',
      syncing: 'Syncing...',
      productTitle: 'Title',
      brand: 'Brand',
      condition: 'Condition',
      availability: 'Availability',
      approvalStatus: 'Approval Status',
      offerId: 'Offer ID',
      performance: 'Performance',
      insights: 'Insights',
      noProducts: 'No products found. Enter your Merchant ID and sync.',
    },
    shopping: {
      title: 'Shopping Campaigns',
      settings: 'Shopping Settings',
      merchantId: 'Merchant ID',
      feedLabel: 'Feed Label',
      priority: 'Priority',
      enableLocal: 'Enable Local Inventory',
      saveSettings: 'Save Settings',
      saving: 'Saving...',
      assetGroups: 'Asset Groups',
      createAssetGroup: 'Create Asset Group',
      name: 'Name',
      status: 'Status',
      finalUrls: 'Final URLs (one per line)',
      headlines: 'Headlines (one per line)',
      descriptions: 'Descriptions (one per line)',
      listingGroups: 'Listing Groups',
      dimensionType: 'Dimension Type',
      caseValue: 'Case Value',
      noAssetGroups: 'No asset groups found.',
      noListingGroups: 'No listing groups found.',
    },
    audiences: {
      title: 'Audience Manager',
      segmentsTab: 'Segments',
      targetingTab: 'Campaign Targeting',
      remarketingTab: 'Remarketing Tags',
      name: 'Name',
      type: 'Type',
      sourceType: 'Source Type',
      sizeEstimate: 'Size Estimate',
      status: 'Status',
      description: 'Description',
      createSegment: 'Create Segment',
      bidModifier: 'Bid Modifier',
      targetingSetting: 'Targeting Setting',
      attachAudience: 'Attach Audience',
      detach: 'Detach',
      tagName: 'Tag Name',
      tagType: 'Tag Type',
      snippet: 'Snippet',
      createTag: 'Create Tag',
      noSegments: 'No audience segments found.',
      noAudiences: 'No audiences attached to this campaign.',
      noTags: 'No remarketing tags found.',
    },
    bidRules: {
      title: 'Bid Rules',
      evaluateAll: 'Evaluate All Rules',
      evaluating: 'Evaluating...',
      name: 'Name',
      ruleType: 'Rule Type',
      active: 'Active',
      conditions: 'Conditions (JSON)',
      actions: 'Actions (JSON)',
      lastEvaluated: 'Last Evaluated',
      createRule: 'Create Rule',
      editRule: 'Edit Rule',
      logs: 'Execution Logs',
      timestamp: 'Timestamp',
      actionTaken: 'Action Taken',
      oldValue: 'Old Value',
      newValue: 'New Value',
      triggered: 'Triggered',
      noRules: 'No bid rules created yet.',
      noLogs: 'No execution logs for this rule.',
    },
    recommendations: {
      title: 'Recommendations',
      generate: 'Generate Recommendations',
      generating: 'Generating...',
      allCampaigns: 'All Campaigns',
      apply: 'Apply',
      dismiss: 'Dismiss',
      impact: 'Impact',
      priority: 'Priority',
      noRecommendations: 'No recommendations yet. Click Generate to analyze your campaigns.',
    },
    nav: {
      performance: 'Performance',
      sync: 'Google Ads Sync',
      keywordResearch: 'Keyword Research',
      negativeKeywords: 'Negative Keywords',
      searchTerms: 'Search Terms',
      auctionInsights: 'Auction Insights',
      products: 'Products',
      shopping: 'Shopping Campaigns',
      audiences: 'Audiences',
      bidRules: 'Bid Rules',
      recommendations: 'Recommendations',
    },
    campaigns: {
      title: 'Campaigns',
      subtitle: 'Manage your search advertising campaigns',
      create: 'Create Campaign',
      totalSpend: 'Total Spend',
      totalClicks: 'Total Clicks',
      activeCampaigns: 'Active',
      allCampaigns: 'All Campaigns',
      campaignName: 'Campaign',
      platform: 'Platform',
      budget: 'Budget',
      spend: 'Spend',
      impressions: 'Impressions',
      clicks: 'Clicks',
      matchType: 'Match Type',
      bid: 'Bid',
      qualityScore: 'Quality Score',
      keyword: 'Keyword',
      addKeyword: 'Add Keyword',
      totalBudget: 'Total daily budget',
      totalCampaigns: 'Total campaigns',
      ofTotal: 'of',
      total: 'total',
      empty: 'No campaigns yet — connect Google Ads and run a sync.',
      perfHint: 'Metrics in Performance',
      newCampaign: 'New Campaign',
      newCampaignDesc: 'Create a new advertising campaign',
      wizardDesc: 'Create a complete advertising campaign with AI assistance',
      step1: 'Describe',
      step2: 'AI Suggestions',
      step3: 'Settings',
      step4: 'Review',
      productService: 'Product / Service',
      productServicePlaceholder: 'e.g. Handcrafted leather bags for women — made in Germany',
      targetAudience: 'Target audience',
      targetAudiencePlaceholder: 'e.g. women 25–45, premium segment',
      usps: 'Unique selling points',
      uspsPlaceholder: 'e.g. free shipping, 2-year warranty, vegan materials',
      tone: 'Tone',
      describeRequired: 'Please describe your product or service first',
      generate: 'Generate AI Suggestions',
      generating: 'Generating...',
      regenerate: 'Regenerate',
      suggestions: 'Suggestions for you',
      aiIntro: 'AI analyzes your input and suggests a campaign name, keywords, ad copy and bidding strategy',
      suggestError: 'Could not generate suggestions. Please try again.',
      generateFirst: 'Generate AI suggestions or enter a name first',
      keywordSuggestions: 'Keywords',
      adCopySuggestions: 'Ad copy',
      biddingSuggestions: 'Bidding strategy',
      campaignType: 'Campaign type',
      status: 'Status',
      finalUrl: 'Final URL',
      landingHint: 'Hint',
      startDate: 'Start date',
      endDate: 'End date',
      targetCpa: 'Target CPA',
      targetRoas: 'Target ROAS',
      googleAdsCustomerId: 'Google Ads Customer ID',
      biddingStrategy: 'Bidding',
      keywords: 'Keywords',
      adCopy: 'Ad copy',
      namePlaceholder: 'Campaign name',
      perDay: '/day',
      creating: 'Creating...',
      nameRequired: 'Campaign name is required',
      createError: 'Failed to create campaign',
      aiCreditsNotice: 'AI processing used ({{model}}) — costs are being charged',
      aiFallbackNotice: 'No AI model configured — rule-based suggestions used',
      aiSpend: 'AI spend',
      aiCalls: 'AI calls',
      push: 'Push to Google Ads',
      pushTooltip: 'Push campaign to Google Ads',
      pushError: 'Push failed. Make sure a Google Ads customer ID is set and the Google Ads account is connected.',
      pushSuccess: 'Campaign pushed to Google Ads successfully',
      externalId: 'Google Ads ID',
      pushAfterCreate: 'Directly push to Google Ads',
      pushAfterCreateHint: 'Requires a Google Ads customer ID and connected account. New campaigns are created as PAUSED in Google Ads.',
      edit: 'Edit campaign',
      editCampaign: 'Edit Campaign',
      editCampaignDesc: 'Update the draft or pause a campaign before pushing it to Google Ads',
      updateError: 'Failed to update campaign',
      saving: 'Saving...',
      save: 'Save',
      customerIdHint: 'Customer ID needs exactly 10 digits',
    },
  },
  automation: {
    dashboard: {
      title: 'Automation Dashboard',
      totalWorkflows: 'Total Workflows',
      activeWorkflows: 'Active Workflows',
      contentPieces: 'Content Pieces',
      reports: 'Reports',
      recentExecutions: 'Recent Executions',
      workflowsByStatus: 'Workflows by Status',
      contentByType: 'Content by Type',
      reportsGenerated: 'Reports Generated',
      noExecutions: 'No recent executions.',
      stepsProgress: 'Steps',
    },
    workflows: {
      title: 'Automation Workflows',
      newWorkflow: 'New Workflow',
      name: 'Workflow Name',
      trigger: 'Trigger',
      schedule: 'Schedule',
      event: 'Event',
      manual: 'Manual',
      isActive: 'Active',
      lastRun: 'Last Run',
      execute: 'Execute',
      create: 'Create Workflow',
      noWorkflows: 'No workflows yet. Create your first automation workflow.',
    },
    webhooks: {
      triggerHistory: 'Trigger History',
      eventType: 'Event Type',
      status: 'Status',
      details: 'Details',
      noHistory: 'No trigger history.',
      fired: 'Fired',
      skipped: 'Skipped',
      failed: 'Failed',
    },
    content: {
      title: 'Content Generator',
      generate: 'Generate Content',
      type: 'Content Type',
      blogPost: 'Blog Post',
      metaDescription: 'Meta Description',
      adCopy: 'Ad Copy',
      socialPost: 'Social Post',
      topic: 'Topic',
      targetKeywords: 'Target Keywords',
      seoScore: 'SEO Score',
      status: 'Status',
      draft: 'Draft',
      review: 'In Review',
      published: 'Published',
      promptPlaceholder: 'Describe the content you want to generate...',
      generating: 'Generating...',
      noContent: 'No content generated yet.',
      contentType: 'Content Type',
      keywords: 'Keywords',
      topicPlaceholder: 'Enter topic or title...',
      keywordsPlaceholder: 'keyword1, keyword2, keyword3...',
      types: {
        blogPost: 'Blog Post',
        metaDescription: 'Meta Description',
        adCopy: 'Ad Copy',
        socialPost: 'Social Post',
      },
    },
    reports: {
      title: 'Reports',
      generate: 'Generate Report',
      name: 'Report Name',
      type: 'Report Type',
      format: 'Format',
      seoOverview: 'SEO Overview',
      campaignPerformance: 'Campaign Performance',
      competitorAnalysis: 'Competitor Analysis',
      custom: 'Custom Report',
      download: 'Download',
      noReports: 'No reports generated yet.',
      reportName: 'Report Name',
      reportNamePlaceholder: 'Enter report name...',
      reportType: 'Report Type',
      generateDownload: 'Generate & Download',
      downloadPdf: 'Download PDF',
      emptyTitle: 'No reports generated',
      emptyDesc: 'Generate your first report to track progress and share insights',
      executionHistory: 'Execution History',
      noExecutions: 'No executions yet. Click "Generate Report" to start.',
      exportFailed: 'PDF export failed',
      exportSuccess: 'PDF downloaded',
      status: {
        generating: 'Generating',
        completed: 'Completed',
        failed: 'Failed',
      },
      types: {
        seo: 'SEO Report',
        sea: 'SEA Report',
        bundled: 'Bundled Report',
        seoAudit: 'SEO Audit',
        campaignPerformance: 'Campaign Performance',
        keywordRanking: 'Keyword Ranking',
        competitor: 'Competitor Analysis',
        custom: 'Custom Report',
      },
      schedules: {
        title: 'Scheduled Reports',
        create: 'Create Schedule',
        frequency: 'Frequency',
        deliveryMethod: 'Delivery Method',
        recipients: 'Recipients (comma-separated emails)',
        runNow: 'Run Now',
        noSchedules: 'No scheduled reports yet.',
        frequencies: {
          weekly: 'Weekly',
          monthly: 'Monthly',
          quarterly: 'Quarterly',
        },
        delivery: {
          email: 'Email',
          download: 'Download',
          both: 'Email & Download',
        },
      },
    },
  },
  settings: {
    title: 'Settings',
    description: 'Manage your application configuration across all services.',
    adminOnly: 'Admin only',
    tabs: {
      general: 'General',
      proxy: 'Proxy',
      crawler: 'Crawler',
      seo: 'SEO',
      sea: 'SEA',
      marketing: 'Marketing',
      email: 'Email',
      ai: 'AI & Models',
      users: 'Users',
      generalHint: 'General application settings.',
      proxyHint: 'Configure proxy settings used for web crawling.',
      crawlerHint: 'Configure crawl engine behaviour, concurrency and limits.',
      seoHint: 'SEO-related global settings.',
      seaHint: 'SEA / advertising campaign settings.',
      marketingHint: 'Marketing automation settings.',
      emailHint: 'SMTP email delivery settings.',
      aiHint: 'Manage API keys and choose which AI model is used for each task.',
      usersHint: 'Manage users, roles and passwords.',
    },
    save: 'Save Settings',
    saved: 'Settings saved successfully',
    noSettings: 'No settings found.',
    noChanges: 'No changes to save.',
    saveError: 'Failed to save some settings.',
    settingCount: {
      one: 'setting',
      other: 'settings',
    },
    categories: {
      API: 'API Keys',
      PROXY: 'Proxy',
      CRAWLER: 'Crawler',
      GENERAL: 'General',
      LOGGING: 'Logging',
      EMAIL: 'Email',
      SEO: 'SEO',
      SEA: 'SEA',
      MARKETING: 'Marketing',
    },
    source: {
      database: 'Database',
      environment: 'Environment',
      default: 'Default',
      none: 'Not set',
    },
  },
  profile: {
    title: 'Profile',
    username: 'Username',
    email: 'Email',
    role: 'Role',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    updateProfile: 'Update Profile',
    updated: 'Profile updated successfully',
    details: 'Profile Details',
    name: 'Name',
    updateError: 'Failed to update profile.',
    passwordChanged: 'Password changed successfully.',
    passwordError: 'Failed to change password.',
  },
  aiAdvisor: {
    title: 'AI Advisor',
    askQuestion: 'Ask a question...',
    suggestions: 'Suggestions',
    seoTips: 'SEO Tips',
    campaignInsights: 'Campaign Insights',
    contentIdeas: 'Content Ideas',
    analyzing: 'Analyzing...',
    unavailable: 'AI service is currently unavailable. Please try again.',
  },
  logs: {
    title: 'System Logs',
    container: 'Container',
    level: 'Level',
    message: 'Message',
    timestamp: 'Timestamp',
    allContainers: 'All Containers',
    allLevels: 'All Levels',
    liveStream: 'Live Stream',
    refresh: 'Refresh',
    noLogs: 'No log entries available.',
  },
  auth: {
    login: {
      title: 'Sign In',
      subtitle: 'Sign in to your Agentic SEO account',
      credentials: 'Credentials',
      username: 'Username',
      email: 'Username or Email',
      password: 'Password',
      submit: 'Sign In',
      noAccount: "No account? Contact your administrator for an invitation.",
      apiKeyLogin: 'Sign in with API Key',
      apiKey: 'API Key',
      apiKeyLabel: 'Your API Key',
      error: 'Invalid credentials. Please try again.',
    },
    register: {
      inviteOnlyTitle: 'Invite-Only Access',
      inviteOnlyDescription: 'Account creation is managed by your administrator. Please contact them to receive an invitation link.',
      backToLogin: 'Back to Login',
    },
  },
  common: {
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    loadFailed: 'Could not load data',
    success: 'Success',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search...',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    refresh: 'Refresh',
    noData: 'No data available',
    confirm: 'Are you sure?',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    failed: 'Failed',
    running: 'Running',
    paused: 'Paused',
    total: 'Total',
    actions: 'Actions',
    name: 'Name',
    description: 'Description',
    status: 'Status',
    date: 'Date',
    createdAt: 'Created',
    updatedAt: 'Updated',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    page: 'Page',
    of: 'of',
    showing: 'Showing',
    results: 'results',
    creating: 'Creating...',
    addUrl: 'Add URL',
    goBack: 'Go Back',
    backToProjects: 'Back to Projects',
    saving: 'Saving...',
    copy: 'Copy',
    today: 'Today',
    yesterday: 'Yesterday',
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'This feature is currently under development and will be available soon.',
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    exporting: 'Exporting...',
  },
  feedback: {
    title: 'Send Feedback',
    type: 'Feedback Type',
    bugReport: 'Bug Report',
    featureRequest: 'Feature Request',
    improvement: 'Improvement',
    question: 'Question',
    titleField: 'Title',
    titlePlaceholder: 'Brief summary...',
    description: 'Description',
    descriptionPlaceholder: 'Tell us more...',
    attachment: 'Attachment',
    submit: 'Submit Feedback',
    success: 'Thank you for your feedback!',
    close: 'Close',
  },
  abTesting: {
    title: 'A/B Testing',
    newTest: 'New Test',
    variants: 'Variants',
    trafficSplit: 'Traffic Split',
    winner: 'Winner',
    confidence: 'Confidence',
    adCopy: 'Ad Copy Test',
    landingPage: 'Landing Page Test',
    content: 'Content Test',
    declareWinner: 'Declare Winner',
    significanceReached: 'Statistical significance reached (95%+ threshold)',
    noTests: 'No A/B tests yet. Create your first test to get started.',
    awaitingData: 'Collecting data...',
  },
  workflowHistory: {
    title: 'Execution History',
    workflow: 'Workflow',
    started: 'Started',
    duration: 'Duration',
    triggeredBy: 'Triggered By',
    rerun: 'Re-run',
    viewLogs: 'View Logs',
    noExecutions: 'No workflow executions yet.',
  },
  roadmap: {
    title: 'Product Roadmap',
    subtitle: 'See what we\'re building and what\'s coming next',
    requestFeature: 'Request Feature',
    boardView: 'Board',
    timelineView: 'Timeline',
    planned: 'Planned',
    inprogress: 'In Progress',
    completed: 'Completed',
    underreview: 'Under Review',
    items: {
      seoAudit: 'Comprehensive crawling, scoring, and recommendations engine',
      googleAds: 'Direct Google Ads API integration for campaign management',
      aiConnector: 'Unified AI provider connector for Claude, Gemini, and OpenAI integrations',
      reporting: 'Scheduled PDF/email reports with custom templates',
      multiLang: 'Support for multi-language SEO analysis and hreflang management',
      budgetOptimizer: 'AI-driven budget allocation across campaigns',
      competitorIntel: 'Deep competitor analysis with automated insights',
      workflowBuilder: 'Visual drag-and-drop workflow creation tool',
      socialMedia: 'Publish, schedule, and track social media content across all major platforms',
    },
  },
  admin: {
    totalUsers: 'Total Users',
    createUser: 'Create User',
    autoPassword: 'A secure password will be generated automatically.',
    resetPassword: 'Reset Password',
    applyPassword: 'Apply Password',
    role: 'Role',
    userCreated: 'User created successfully',
    userCreatedPassword: 'Generated password (copy it now — shown only once):',
    userCreateError: 'Failed to create user.',
    usersLoadError: 'Failed to load users.',
    roleChanged: 'Role updated successfully',
    roleChangeError: 'Failed to update role.',
    passwordGenerateError: 'Failed to generate a secure password.',
    passwordReset: 'Password reset successfully',
    passwordResetError: 'Failed to reset password.',
    userDeleted: 'User deleted successfully',
    userDeleteError: 'Failed to delete user.',
    deleteUserTitle: 'Delete user?',
    deleteUserDescription: 'This will permanently remove {{name}} and all associated data.',
  },
  templates: {
    title: 'Templates',
    subtitle: 'Manage reusable content templates with dynamic variables',
    projectSubtitle: 'Templates for this project',
    create: 'Create Template',
    namePlaceholder: 'Template name...',
    contentPlaceholder: 'Write your template content here. Use variables like {{project_name}} for dynamic values...',
    insertVariable: 'Insert variable',
    editor: 'Editor',
    preview: 'Preview',
    emptyPreview: 'Preview will appear here...',
    variables: 'variables',
    empty: 'No templates yet. Create your first template.',
    global: 'Global',
    typeEmail: 'Email',
    typeReport: 'Report',
    typeNotification: 'Notification',
    typeSEOBrief: 'SEO Brief',
  },
  integrations: {
    title: 'Integrations',
    subtitle: 'Connect your accounts and manage API keys',
    oauthSection: 'OAuth Connections',
    oauthDescription: 'Connect your accounts to enable data syncing across all projects',
    apiKeySection: 'API Key Integrations',
    apiKeyDescription: 'Add API keys for SEO tools and data providers',
    connect: 'Connect',
    disconnect: 'Disconnect',
    refresh: 'Refresh Tokens',
    addApiKey: 'Add API Key',
    removeApiKey: 'Remove',
    connected: 'Connected',
    disconnected: 'Not Connected',
    expired: 'Expired',
    error: 'Error',
    connectedAs: 'Connected as',
    connectedSince: 'Connected since',
    lastRefreshed: 'Last refreshed',
    confirmDisconnect: 'Are you sure? This will remove all project integrations using this connection.',
    apiKeyLabel: 'Label (optional)',
    apiKeyPlaceholder: 'Enter your API key',
    apiSecretPlaceholder: 'Enter API secret (if required)',
    provider: 'Provider',
    infoText: 'Connect your accounts here. Once connected, you can assign specific properties to individual projects from the project settings.',
    adminOnly: 'Only administrators can manage connections',
    credentialsNotConfigured: 'OAuth credentials are not configured. Expand the provider card and add your Client ID & Secret first.',
    credentialsHintTitle: 'Configure OAuth Credentials First',
    credentialsHintDescription: 'Before connecting accounts, expand the provider card below and enter your OAuth Client ID & Secret.',
    connectError: 'Failed to initiate connection',
    connectAnother: 'Connect another',
    noConnections: 'No accounts connected yet',
    noApiKey: 'No API key configured yet',
    lastVerified: 'Last verified',
    test: 'Test',
    testConnection: 'Test Connection',
    remove: 'Remove',
    // OAuth App Configuration
    oauthConfig: 'OAuth App Configuration',
    oauthConfigDesc: 'Configure OAuth credentials for each provider. You can use the platform-provided defaults or enter your own app credentials.',
    redirectUri: 'Redirect URI',
    redirectUriHint: 'Add this redirect URI to your OAuth app configuration in the provider\'s developer console.',
    copyUrl: 'Copy',
    urlCopied: 'Copied to clipboard',
    credentialStatus: 'Credential Status',
    configured: 'Configured',
    notConfigured: 'Not configured',
    sourceEnvironment: 'Platform',
    sourceDatabase: 'Custom',
    configureProvider: 'Configure',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    saveCredentials: 'Save Credentials',
    savingCredentials: 'Saving…',
    credentialsSaved: 'Credentials saved successfully',
    credentialsSaveError: 'Failed to save credentials',
    showConfig: 'Show Configuration',
    hideConfig: 'Hide Configuration',
  },
  projectSettings: {
    title: 'Project Settings',
    tabs: {
      general: 'General',
      settings: 'Settings',
      dataSources: 'Data Sources',
      connectors: 'Connectors',
      competitors: 'Competitors',
      sea: 'SEA',
    },
    sea: {
      title: 'SEA Settings',
      subtitle: 'Central Google Ads configuration used by all campaigns in this project',
      googleAdsCustomerId: 'Google Ads Customer ID',
      customerIdHint: 'Customer ID needs exactly 10 digits',
      globalValue: 'Global',
      save: 'Save',
      saved: 'Google Ads customer ID saved',
      resetButton: 'Reset to global',
      reset: 'Override removed — using global value',
      loadError: 'Failed to load SEA settings',
      saveError: 'Failed to save customer ID',
      overrideActive: 'A project-specific Google Ads customer ID is active for this project.',
      customerIdDesc: '10-digit Google Ads account ID. Leave empty to use the global value.',
    },
    settings: {
      title: 'Project Settings',
      description: 'Override global settings for this project. Values inherit from global settings unless overridden.',
      categoryHint: 'Project overrides for this category. Leave blank to inherit the global value.',
      reset: 'Reset to global',
      globalValue: 'Global',
    },
    settingsSaved: 'Settings saved',
    settingsReset: 'Reset to global',
    source: {
      project: 'Project override',
      database: 'Database',
      environment: 'Environment',
      default: 'Default',
      none: 'None',
    },
    general: {
      name: 'Project Name',
      url: 'Website URL',
      language: 'Language',
      save: 'Save Changes',
      saved: 'Project updated successfully',
    },
    dataSources: {
      title: 'Data Sources',
      description: 'Connect your analytics and advertising accounts to this project.',
      connectFirst: 'Connect {{provider}} first on the Integrations page',
      goToIntegrations: 'Go to Integrations',
      selectProperty: 'Select property...',
      configure: 'Save',
      disconnect: 'Disconnect',
      connected: 'Connected',
      configuring: 'Saving...',
      noProperties: 'No properties found for this account',
      tokenExpired: 'Token expired — please refresh your connection',
      manage: 'Manage Data Sources',
      adsDeveloperToken: 'Google Ads Developer Token (Project Override)',
      adsDeveloperTokenPlaceholder: 'Project-specific Developer Token (optional)',
      adsOverrideSave: 'Save',
      adsOverrideHint: 'Optional: overrides the global Google Ads developer token for this project. Leave empty to remove the override.',
      adsOverrideError: 'Failed to save override',
    },
    competitors: {
      title: 'Competitors',
      description: 'Add competitor websites to track and compare.',
      addCompetitor: 'Add Competitor',
      urlPlaceholder: 'https://competitor.com',
      remove: 'Remove',
      empty: 'No competitors added yet',
    },
    connectors: {
      title: 'Connectors',
      description: 'Enable or disable connectors for this project. Disabled connectors will not be used.',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enable: 'Enable',
      disable: 'Disable',
      reset: 'Reset to global default',
      overridden: 'Project override',
      inheritsGlobal: 'Inherits global setting',
      empty: 'No connectors available yet. Connect integrations and enable connectors globally (Integrations → Connectors) first.',
    },
  },
  connectors: {
    title: 'Connectors',
    description: 'Manage available connector types. Connectors enabled here are available to all projects.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'Enable',
    disable: 'Disable',
    save: 'Save',
    saved: 'Connector updated',
    category: {
      seo: 'SEO',
      analytics: 'Analytics',
      sea: 'Advertising',
      social: 'Social Media',
      other: 'Other',
    },
    authType: {
      oauth: 'OAuth',
      api_key: 'API Key',
      none: 'None',
    },
    globallyDisabled: 'Globally disabled',
    notAvailable: 'Not available',
    notAvailableHint: 'Connect the integration first to enable this connector.',
    noConnectors: 'No connector definitions found. Please check the backend seed.',
  },
  // AI Copilot page
  aiCopilot: {
    title: 'AI Copilot',
    subtitle: 'Your intelligent assistant for marketing tasks',
    welcome: 'How can I help you today?',
    welcomeDescription: 'Ask me anything about your SEO, ads, or marketing strategy for this project.',
    placeholder: 'Ask about SEO, ads, or marketing strategy...',
  },
  // Workflows page
  workflows: {
    title: 'Workflows',
    subtitle: 'Automate your marketing tasks with custom workflows',
    create: 'Create Workflow',
    templates: 'Workflow Templates',
    useTemplate: 'Use Template',
    steps: 'steps',
    emptyTitle: 'No workflows yet',
    emptyDesc: 'Create your first automation workflow to streamline your marketing tasks',
  },
  // Social page
  social: {
    title: 'Social Media',
    subtitle: 'Manage your social media presence and schedule posts',
    createPost: 'Create Post',
    totalFollowers: 'Total Followers',
    avgEngagement: 'Avg. Engagement',
    scheduledPosts: 'Scheduled Posts',
    connectedPlatforms: 'Connected Platforms',
    upcomingPosts: 'Upcoming Posts',
  },
  // Ads Dashboard page
  adsDashboard: {
    title: 'Ads Dashboard',
    subtitle: 'Monitor and optimize your advertising campaigns',
    createCampaign: 'Create Campaign',
    totalSpend: 'Total Spend',
    totalClicks: 'Total Clicks',
    conversions: 'Conversions',
    roas: 'ROAS',
    campaigns: 'Active Campaigns',
    campaignName: 'Campaign',
    platform: 'Platform',
    budget: 'Budget',
    spend: 'Spend',
    impressions: 'Impressions',
    clicks: 'Clicks',
    totalImpressions: 'Total Impressions',
    campaignCount: 'Campaigns',
    noCampaigns: 'No campaigns yet — connect Google Ads and run a sync.',
    detailHint: 'Details in Performance',
  },
  seoSchedules: {
    title: 'SEO Schedules',
    subtitle: 'Manage all automated checks for this project',
    crawlSection: 'Crawl Schedules',
    crawlSectionDesc: 'Automated site crawls — triggers a full SEO audit pipeline',
    serpSection: 'SERP Check Configs',
    serpSectionDesc: 'Named keyword check sets with device, location, and schedule',
    keywordSection: 'Keyword Check Schedules',
    keywordSectionDesc: 'Scheduled automated keyword rank checks',
    backlinkSection: 'Backlink Schedules',
    backlinkSectionDesc: 'Scheduled backlink profile checks — master + per competitor',
    noSchedule: 'No schedule set',
    nextRun: 'Next run',
    lastRun: 'Last run',
    nextRunLabel: 'next',
    lastRunLabel: 'last',
    manageSchedules: 'Manage schedules',
    setUpSchedule: 'Set up',
    configsActive: 'active configs',
    addCrawlSchedule: 'New Crawl Schedule',
    addBacklinkSchedule: 'Add Schedule',
    addKeywordSchedule: 'Add Keyword Schedule',
    newConfig: 'New Config',
    masterDomain: 'Your domain',
    competitorLabel: 'Competitor',
    runNow: 'Run now',
    editSchedule: 'Edit',
    noCompetitors: 'No competitors configured for this project.',
    statusChipManage: '→ Manage',
    lastRuns: 'Last runs',
    noRunHistory: 'No run history',
  },

  // Phase B-D feature namespaces
  keywordMap: {
    title: 'Keyword Mapping',
    subtitle: 'Map keywords to pages and detect cannibalization.',
    runAlgo: 'Run Algorithm',
    runAi: 'Run AI Analysis',
    noMaps: 'No keyword maps yet.',
  },
  keywordResearch: {
    title: 'Keyword Research',
    subtitle: 'Discover and analyse keyword opportunities.',
    query: 'Search keywords…',
    noResults: 'No results found.',
  },
  contentOptimization: {
    title: 'Content Optimization',
    subtitle: 'AI-powered content improvement suggestions.',
    accept: 'Accept',
    dismiss: 'Dismiss',
    noSuggestions: 'No suggestions yet.',
    workbench: 'Workbench',
    generateBrief: 'Generate Brief',
    generatingBrief: 'Generating brief…',
    briefGenerated: 'Brief generated successfully',
    focusKeyword: 'Focus Keyword',
    secondaryKeywords: 'Secondary Keywords',
    scoreBefore: 'Score Before',
    scoreAfter: 'Score After',
    geoAeoScore: 'GEO/AEO Score',
    recalculate: 'Recalculate',
    acceptSection: 'Accept Section',
    rejectSection: 'Reject',
    regenerate: 'Regenerate',
    verifyFlags: 'Verify Flags',
    schemaPreview: 'Schema Preview',
    newPreset: 'New Preset',
    builtinPreset: 'Built-in',
    editPreset: 'Edit Preset',
    deletePreset: 'Delete Preset',
    pageType: 'Page Type',
    minWords: 'Min. Words',
    opportunities: 'Content Opportunities',
    presets: 'Presets',
    tier1: 'Quick Wins',
    tier2: 'High Potential',
    tier3: 'Needs Work',
    tier4: 'Low Priority',
    opportunityScore: 'Opportunity Score',
    noOpportunities: 'No optimization opportunities found.',
    createProject: 'Create Project',
    all: 'All',
    projects: 'My Projects',
    noProjects: 'No optimization projects yet.',
    backToOpportunities: '← Opportunities',
    openProject: 'Open Project',
    viewAllProjects: 'View all projects',
    costNoticeLabel: 'AI Processing:',
    costNotice: 'Uses your configured AI provider — costs are billed against your API key.',
    dataforseoPaidBadge: 'Paid DataForSEO API',
    loadingKeywordMetrics: 'Loading keyword metrics…',
    dataforseoConsentTitle: 'Enable paid DataForSEO analysis?',
    dataforseoConsentBody: 'SERP competitor analysis and keyword metrics use the paid DataForSEO API. Each lookup consumes credits from your DataForSEO account. Costs are tracked in the API spending dashboard.',
    dataforseoConsentAccept: 'Enable & continue',
    dataforseoConsentDecline: 'Not now',
    dataforseoConsentHint: 'Enable DataForSEO for competitor keywords & keyword metrics.',
    urlKeywords: 'URL Keywords',
    suggestedKeywords: 'Suggested from URL',
    briefReady: 'Brief is ready!',
    briefPending: 'Brief generation in progress…',
    briefTimeout: 'Brief generation is taking long. Refresh to check status.',
    saveKeywords: 'Save Keywords',
    backToProjects: '← My Projects',
    generateOutline: 'Generate Outline',
    runPipeline: 'Run Optimization Loop',
    runIteration: 'Run One Iteration',
    finalize: 'Finalize',
    publishReady: 'Publish-ready',
    publishNotReady: 'Needs more work',
    iterationHistory: 'Iteration History',
    contentSourceScraped: 'Optimizing existing (scraped) content',
    contentSourceScratch: 'New content — writing from scratch',
    keywordsFromMap: 'from Keyword Mapping',
    tertiaryKeywords: 'Tertiary Keywords',
    feedback: 'Feedback',
    scoreDelta: 'Score delta',
    maxIterations: 'Max iterations',
    noIterations: 'No iterations yet — run the loop to see progress.',
    pipelineQueued: 'Pipeline queued — outline → draft → loop → finalize.',
    iterationQueued: 'One iteration queued.',
    finalizeQueued: 'Finalize queued.',
    recontextQueued: 'Context rebuild queued.',
    loopScore: 'Overall Score',
    exportTitle: 'Export',
    copyHtml: 'Copy HTML',
    copyText: 'Copy Text',
    copied: 'Copied!',
    downloadReport: 'Report',
    downloadContent: 'Content',
    downloadPdf: 'Download PDF',
    optimizationTerms: 'Optimization terms',
    termsMet: '{{met}}/{{total}} met',
    termsHint: 'Target = competitor-derived occurrence goal. Green = reached.',
    fromSerp: 'SERP',
    similarity: 'Similarity',
    similarityVsExisting: 'vs existing page',
    similaritySeoImprovement: 'SEO improvement',
    similarityTooSimilar: 'Too similar to the existing page — consider a stronger rewrite.',
    keywordsTitle: 'Keywords',
    outlineTitle: 'Outline',
    serpAnalysisTitle: 'SERP Analysis',
    aiWriting: 'AI Writing',
    scoreTitle: 'Score',
    edit: 'Edit',
    refresh: 'Refresh',
    defineKeywordsFirst: 'Define keywords first',
    researchingSerp: 'Researching competitors…',
  },
  urlHygiene: {
    title: 'URL Hygiene',
    issues: 'Issues',
    noIssues: 'No URL hygiene issues.',
  },
  migration: {
    title: 'Migration / Compare',
    subtitle: 'Map old URLs to new and compare structural changes.',
    create: 'Create Migration',
    runMatch: 'Run URL Match',
    runCompare: 'Run Compare',
    noMigrations: 'No migrations yet.',
  },
  tasks: {
    title: 'Smart To-Do',
    subtitle: 'Swipe right to complete, left to defer, up to schedule.',
    generate: 'Generate AI Tasks',
    generateGeo: 'GEO Best Practices',
    geoGenerated: '{{n}} GEO best-practice tasks added.',
    geoGeneratedNone: 'GEO best-practice tasks already exist.',
    generated: 'AI tasks generated',
    generatedQueued: 'Task generation queued — tasks will appear shortly.',
    costHint: 'AI task generation uses your configured AI provider and is billed against your API key. It runs in the background — tasks appear when ready.',
    noTasks: 'No tasks yet.',
    open: 'Open',
    inProgress: 'In Progress',
    done: 'Done',
  },
  geo: {
    title: 'GEO — Generative Engine Optimization',
    subtitle: 'Monitor LLM mentions and AI visibility across platforms.',
    refresh: 'Refresh Mentions',
    noMentions: 'No LLM mentions tracked yet.',
    checklist: 'Best Practices Checklist',
    faqAudit: 'FAQ Audit',
  },
  visualAnalysis: {
    title: 'Visual Analysis',
    subtitle: 'AI-powered UX and SEO critique from page screenshots.',
    analyze: 'Analyse Page Types',
    noAnalyses: 'No analyses yet.',
    pageTypes: 'Page Types',
    results: 'Results',
    hypotheses: 'Hypotheses',
    autoDetected: 'auto-detected',
    competitorNotice: 'Competitors: top-3 SERP pages (paid DataForSEO, consent-gated)',
    visionFallbackToast: 'The configured model does not support image analysis — showing crawl-data analysis instead.',
    queued: 'Analysis queued — check the Results tab shortly.',
    analyzeError: 'Failed to enqueue analysis.',
    hypothesisStarted: 'Test started — GA4 baseline captured.',
    hypothesisConcluded: 'Test concluded — results measured.',
    hypothesisMeasured: 'Results refreshed.',
    hypothesisError: 'Action failed.',
    requiresCompetitors: 'Competitor comparison requires DataForSEO consent + a primary keyword.',
    requiresGa4: 'GA4 not connected — skipped.',
    requiresClarity: 'Clarity not connected — skipped.',
  },
  irysIndex: {
    title: 'Irys SEO Index',
    subtitle: 'Composite SEO health score across 7 dimensions.',
    avg: 'Average Score',
    dimensions: 'Dimensions',
    history: 'Score History',
    weights: 'Dimension Weights',
    overallScore: 'Overall',
    regenerate: 'Analyse & generate tasks',
    regenerateQueued: 'Irys analysis queued — competitor benchmark, AI narrative and Smart To-Do tasks are being generated.',
    weightsSaved: 'Weights saved — they apply to the next scoring run.',
    costHint: '"Analyse & generate tasks" runs the AI narrative + Smart To-Do generation (billed to your configured AI provider) and reuses your already-stored SERP + competitor data — no new paid DataForSEO call.',
  },
  spending: {
    title: 'API Spending',
    currentMonth: 'Current Month',
    provider: 'Provider',
    total: 'Total Cost',
    noData: 'No spending data.',
  },

  aiSettings: {
    title: 'AI & Models',
    subtitle: 'Configure API keys and choose which AI models to use for each task.',
    active: 'Active',
    masterKeyNote: ' (Master Key — all calls route through OpenRouter)',
    masterKeyBadge: 'Master Key',
    activeSummary: {
      openrouter: 'All AI calls are routed through OpenRouter. Individual provider keys below are ignored.',
    },
    openRouterMaster: 'OpenRouter API Key (Master)',
    openRouterWarning: '⚠ When set, ALL AI calls go through OpenRouter. Other provider keys are ignored.',
    individualProviderKeys: 'Individual Provider Keys',
    providerKeys: 'Provider API Keys',
    individualProviderKeysHint: 'Add a key for each provider you want to use. The key with the most recent save becomes the active provider.',
    disabledOpenRouter: '(disabled — OpenRouter is active)',
    apiKeyPlaceholder: 'API key...',
    preferredProvider: 'Preferred provider:',
    defaultModelsPerTask: 'Default Models per Task',
    defaultModelsDescription: 'Choose which model to use for each AI-powered feature. Leave empty to use the system preset (Claude).',
    twoDropdownsHint: 'Pick a provider and a model for each touchpoint. Models load live from the provider.',
    groups: {
      seo: 'SEO Service',
      sea: 'SEA / Ads Service',
      automation: 'Automation & Reports',
    },
    groupHints: {
      seo: 'seo-service touchpoints',
      sea: 'sea-service touchpoints',
      automation: 'automation-service touchpoints',
    },
    selectProviderFirst: 'Select a provider first',
    loadingModels: 'Loading models…',
    noModels: 'No models available',
    free: 'free',
    refreshModels: 'Refresh models',
    testProvider: 'Test connection',
    keySet: 'Key set',
    keySetHint: 'leave empty to keep it, type a new one to replace',
    systemPreset: 'System preset (Claude)',
    saveSettings: 'Save Settings',
    resetToDefaults: 'Reset to Defaults',
    settingsSaved: 'Settings saved',
    saveFailed: 'Failed to save',
    resetDone: 'Reset to defaults',
    resetFailed: 'Reset failed',
    taskLabels: {
      migrationMatch: 'URL Migration Matching',
      contentGeneration: 'Content Generation',
      contentOutline: 'Content Outline Generation',
      contentCritique: 'Content Critique / Review',
      contentRewrite: 'Content Rewrite (Loop Fix)',
      metaSuggest: 'Meta Suggestions',
      h1Suggest: 'H1 Suggestions',
      keywordSuggest: 'Keyword Suggestions',
      seoTasksAi: 'SEO Task Generation',
      contentScore: 'Content Scoring',
      visualAnalysis: 'Visual Analysis',
      irysScoring: 'Irys SEO Scoring',
      geoFaq: 'GEO FAQ Audit',
      geoChecklist: 'GEO Checklist',
      seaCampaignSuggest: 'SEA Campaign Suggestions',
      seaAdCopy: 'SEA Ad Copy',
      seaKeywordResearch: 'SEA Keyword Research',
      automationContent: 'Marketing Content Generation',
      reportNarrative: 'Report Narratives / Action Items',
    },
    providerLabels: {
      openrouter: 'OpenRouter (Master Key)',
      anthropic: 'Anthropic (Claude)',
      deepseek: 'DeepSeek',
      openai: 'OpenAI',
      google: 'Google (Gemini)',
    },
    providerDescriptions: {
      openrouter: 'Routes all AI calls through a single gateway with access to many models.',
      anthropic: 'Claude models — the default provider for most SEO tasks.',
      deepseek: 'DeepSeek models for cost-effective generation.',
      openai: 'OpenAI GPT models.',
      google: 'Gemini models from Google.',
    },
  },
} as const;